'use client'

import { C, Callout, Exercise, H, Lesson, OL, P, Step, UL } from '../_components/lesson'
import * as EX from './exercises/lang-extra'

export default function ProjectNameGenerator() {
  return (
    <Lesson>
      <Step>
        <H>Project: invent new names</H>
        <P>
          Over this module you’ve built three language models for names: the bigram table, the MLP with a 3-character window,
          and the RNN with a running memory. Which is actually best? “It looks good” isn’t an answer. In this project you’ll
          compare them properly, then use the winner to generate names that have never existed.
        </P>
        <OL>
          <li>Hold out validation names, and score the bigram baseline.</li>
          <li>Score the MLP and the RNN on the same names, and pick the winner.</li>
          <li>Generate names, and measure how creative the model is at different temperatures.</li>
        </OL>
      </Step>

      <Step gate="name-baseline">
        <H>Part 1 · A fair test and a baseline</H>
        <P>
          The score is the average <strong>loss per character</strong> on names the model never saw: the cross-entropy from
          Module 5, in nats. Lower means the model is less surprised by real names, and less surprise means it understands
          names better. Start with the simplest model.
        </P>
        <Exercise
          id="name-baseline"
          title="bigramValLoss"
          {...EX.nameBaseline}
          hints={[
            <>
              <C>{'const counts = range(27).map(() => new Array(27).fill(1))'}</C>, then count pairs in{' '}
              <C>encode(trainNames)</C>.
            </>,
            <>
              For each validation name: <C>{'const seq = ("." + name + ".").split("").map(c => chars.indexOf(c))'}</C>, and
              add <C>{'-Math.log(counts[seq[i]][seq[i + 1]] / vec.sum(counts[seq[i]]))'}</C> for each step.
            </>,
          ]}
        />
      </Step>

      <Step gate="name-compare">
        <H>Part 2 · The contest</H>
        <P>
          Now the two neural models, trained on the same names and scored on the same held-out names. Both are provided; you
          write the MLP’s scoring and pick the winner.
        </P>
        <Exercise
          id="name-compare" timeoutMs={60000}
          title="mlpValLoss and the winner"
          {...EX.nameCompare}
          hints={[
            <>
              <C>{'const v = contexts(valNames); return mlp(v.X).crossEntropy(v.Y).item()'}</C>
            </>,
            <>
              <C>{'(Object.keys(results) as (keyof typeof results)[]).reduce((a, b) => (results[b] < results[a] ? b : a))'}</C>
            </>,
          ]}
        />
        <Callout kind="plain">
          The gaps are small because there are only 520 training names: about 3 000 characters. With a million names the
          neural models would pull far ahead. This is the core lesson of scaling (Module 9): bigger, better models need more
          data before they can show what they can do.
        </Callout>
      </Step>

      <Step gate="name-sample">
        <H>Part 3 · Generate, and measure creativity</H>
        <P>
          A generator that only reproduces training names has memorised, not learned. Measure <strong>novelty</strong>: the
          fraction of generated names that aren’t real names in the dataset. Then see how temperature trades safety for
          creativity.
        </P>
        <Exercise
          id="name-sample" timeoutMs={60000}
          title="generate and novelty"
          {...EX.nameSample}
          hints={[
            <>
              Start with <C>{'let h = Tensor.zeros(1, H)'}</C> and <C>c = 0</C> (the full stop that marks the start). Each step:{' '}
              <C>{'h = step([c], h)'}</C>, then <C>{'c = random.sample(vec.softmax(head.forward(h).row(0), temperature))'}</C>.
            </>,
            <>
              novelty: keep the non-empty samples, and count the ones where <C>{'!new Set(data.names).has(s)'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>Module 8 complete</H>
        <P>
          You’ve taken text from characters to tokens, counted it, embedded it, and modelled it with tables, MLPs and recurrent
          networks, and learned how to tell which model is really better. Every language model, up to the largest, is a
          scaled-up version of these: predict the next token, measure the surprise, descend the gradient.
        </P>
        <UL>
          <li>Stretch: train the RNN for longer. When does the validation loss start rising again (overfitting)?</li>
          <li>Stretch: increase the RNN’s hidden size from 24 to 64. Better or worse on validation, with this little data?</li>
          <li>
            Stretch: generate 200 names at temperature 1 and plot the distribution of their lengths next to the real names’.
          </li>
        </UL>
      </Step>
    </Lesson>
  )
}
